import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Table, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShippingFast, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { getSpedizioneSito, updateSpedizioneSito } from '../services/api';

const euro = (v) => `${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// Stessa regola del server (services/spese_sito.py): la prima fascia con totale < "fino a", altrimenti l'ultima
function costoFascia(totale, fasce) {
  for (const [limite, costo] of fasce) {
    if (limite === null || totale < limite) return costo;
  }
  return fasce[fasce.length - 1][1];
}

const SpedizioneSitoPage = () => {
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [attiva, setAttiva] = useState(false);
  const [soglia, setSoglia] = useState('29.90');
  const [supplemento, setSupplemento] = useState('1.50');
  // Fasce con limite (righe modificabili) + costo della fascia oltre l'ultimo limite
  const [fasce, setFasce] = useState([{ fino_a: '10', costo: '4.50' }]);
  const [costoOltre, setCostoOltre] = useState('5.50');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getSpedizioneSito();
        setAttiva(!!data.attiva);
        setSoglia(String(data.soglia_gratuita));
        setSupplemento(String(data.supplemento_isole));
        const conLimite = data.fasce.filter(([limite]) => limite !== null);
        const oltre = data.fasce.find(([limite]) => limite === null);
        setFasce(conLimite.map(([limite, costo]) => ({ fino_a: String(limite), costo: String(costo) })));
        setCostoOltre(String(oltre ? oltre[1] : 5.5));
      } catch (err) {
        toast.error('Errore nel caricamento: ' + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fascePayload = useMemo(() => {
    const righe = fasce
      .map(f => [parseFloat(f.fino_a), parseFloat(f.costo)])
      .filter(([limite, costo]) => !isNaN(limite) && !isNaN(costo))
      .sort((a, b) => a[0] - b[0]);
    return [...righe, [null, parseFloat(costoOltre) || 0]];
  }, [fasce, costoOltre]);

  const sogliaNum = parseFloat(soglia) || 0;
  const suppNum = parseFloat(supplemento) || 0;
  const esempi = useMemo(() => {
    const totali = [5, 9.9, 10, 20, Math.max(sogliaNum - 0.01, 0.01), sogliaNum];
    return totali.map(t => ({
      totale: t,
      standard: t >= sogliaNum ? null : costoFascia(t, fascePayload),
      isole: t >= sogliaNum ? null : costoFascia(t, fascePayload) + suppNum,
    }));
  }, [sogliaNum, suppNum, fascePayload]);

  const aggiornaFascia = (i, campo, valore) =>
    setFasce(prev => prev.map((f, idx) => (idx === i ? { ...f, [campo]: valore } : f)));

  const salva = async () => {
    if (!(sogliaNum > 0)) {
      toast.error('La soglia di spedizione gratuita deve essere maggiore di zero.');
      return;
    }
    if (attiva && !window.confirm(
      'La spedizione a pagamento è ATTIVA: sul sito il cliente pagherà la spedizione sotto la soglia.\n' +
      'Assicurati di aver già riportato i prezzi del sito ai prezzi base e di aver aggiornato Merchant Center.\n\nSalvare?')) {
      return;
    }
    setSalvando(true);
    try {
      await updateSpedizioneSito({
        attiva,
        soglia_gratuita: sogliaNum,
        fasce: fascePayload,
        supplemento_isole: suppNum,
      });
      toast.success('Impostazioni di spedizione salvate');
    } catch (err) {
      toast.error('Errore nel salvataggio: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <Container className="py-4 text-center"><Spinner animation="border" /></Container>;
  }

  return (
    <Container fluid className="py-3" style={{ maxWidth: 900 }}>
      <h2 className="h3 mb-1"><FontAwesomeIcon icon={faShippingFast} className="me-2 text-primary" />Spedizione del sito</h2>
      <p className="text-muted small">
        Costo di spedizione per gli ordini del sito in Italia. Il server ricalcola sempre il costo: il valore inviato dal browser non conta.
        La soglia e le fasce valgono sul totale dopo promozioni e sconto cliente, prima del codice sconto.
        L'estero resta ai costi delle regole di spedizione per paese.
      </p>

      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Form.Check
            type="switch"
            id="spedizione-attiva"
            label={<strong>Spedizione a pagamento attiva</strong>}
            checked={attiva}
            onChange={e => setAttiva(e.target.checked)}
          />
          <div className="small text-muted">
            Spenta: la spedizione in Italia è sempre gratuita (prezzi del sito con il costo già incluso). Accesa: si applicano soglia e fasce qui sotto.
          </div>
          {attiva && (
            <Alert variant="warning" className="mt-2 mb-0 py-2 small">
              Con la spedizione a pagamento i prezzi del sito devono essere quelli base (senza il ricarico spedizione) e Merchant Center deve avere le stesse regole.
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm mb-3">
        <Card.Header className="bg-white"><h5 className="mb-0">Costi</h5></Card.Header>
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Label className="fw-semibold">Spedizione gratuita da</Form.Label>
              <div className="input-group">
                <Form.Control type="number" min={0.01} step={0.1} value={soglia} onChange={e => setSoglia(e.target.value)} />
                <span className="input-group-text">€</span>
              </div>
              <Form.Text className="text-muted">Compare anche nell'intestazione del sito.</Form.Text>
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold">Supplemento isole e Sud</Form.Label>
              <div className="input-group">
                <Form.Control type="number" min={0} step={0.1} value={supplemento} onChange={e => setSupplemento(e.target.value)} />
                <span className="input-group-text">€</span>
              </div>
              <Form.Text className="text-muted">Si somma alla fascia. Modena resta al costo della sua regola.</Form.Text>
            </Col>
          </Row>

          <Form.Label className="fw-semibold">Fasce per totale carrello (sotto la soglia)</Form.Label>
          {fasce.map((f, i) => (
            <Row key={i} className="g-2 align-items-center mb-2">
              <Col xs="auto" className="small">Totale sotto</Col>
              <Col xs={4} md={3}>
                <div className="input-group input-group-sm">
                  <Form.Control type="number" min={0.01} step={0.5} value={f.fino_a} onChange={e => aggiornaFascia(i, 'fino_a', e.target.value)} />
                  <span className="input-group-text">€</span>
                </div>
              </Col>
              <Col xs="auto" className="small">spedizione</Col>
              <Col xs={4} md={3}>
                <div className="input-group input-group-sm">
                  <Form.Control type="number" min={0} step={0.1} value={f.costo} onChange={e => aggiornaFascia(i, 'costo', e.target.value)} />
                  <span className="input-group-text">€</span>
                </div>
              </Col>
              <Col xs="auto">
                <Button variant="outline-danger" size="sm" onClick={() => setFasce(prev => prev.filter((_, idx) => idx !== i))} disabled={fasce.length === 0} aria-label="Rimuovi fascia">
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </Col>
            </Row>
          ))}
          <Row className="g-2 align-items-center mb-2">
            <Col xs="auto" className="small">Totale oltre, fino alla soglia: spedizione</Col>
            <Col xs={4} md={3}>
              <div className="input-group input-group-sm">
                <Form.Control type="number" min={0} step={0.1} value={costoOltre} onChange={e => setCostoOltre(e.target.value)} />
                <span className="input-group-text">€</span>
              </div>
            </Col>
          </Row>
          <Button variant="outline-secondary" size="sm" onClick={() => setFasce(prev => [...prev, { fino_a: '', costo: '' }])}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />Aggiungi fascia
          </Button>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm mb-3">
        <Card.Header className="bg-white"><h5 className="mb-0">Anteprima (Italia)</h5></Card.Header>
        <Card.Body className="p-0">
          <Table size="sm" className="mb-0">
            <thead className="table-light">
              <tr><th>Totale carrello</th><th>Standard</th><th>Isole e Sud</th></tr>
            </thead>
            <tbody>
              {esempi.map((e, i) => (
                <tr key={i}>
                  <td>{euro(e.totale)}</td>
                  <td>{e.standard === null ? <span className="text-success">gratuita</span> : euro(e.standard)}</td>
                  <td>{e.isole === null ? <span className="text-success">gratuita</span> : euro(e.isole)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Button variant="primary" onClick={salva} disabled={salvando}>
        {salvando ? <Spinner as="span" animation="border" size="sm" /> : 'Salva'}
      </Button>
    </Container>
  );
};

export default SpedizioneSitoPage;
